import React from "react";

import { useTranslation } from "react-i18next";

import SwitchOnOff from "@/src/components/common/tools/SwitchOnOff";

import SignupTranslateFormInput from "./SignupTranslateFormInput";
import "../../../../css/admin/platform.scss";

import type { JSX } from "react";

import type {
  Locales,
  Project,
  StorefrontProjectParams,
} from "@/src/api/ProjectAPI.types";

export type SignupCustomIndex = 1 | 2 | 3;

// Signup custom fields are persisted as JSON strings under
// `locales[lang].login.custom_N`, e.g. `"{\"label\":\"..\",\"place_holder\":\"..\"}"`.
export interface SignupCustomField {
  label?: string;
  place_holder?: string;
}

interface SignupCustomInputProps {
  index: SignupCustomIndex;
  project: Project;
  changeProject: (project: Project) => void;
  changeProjectParams: (
    key: keyof StorefrontProjectParams,
    value: StorefrontProjectParams[keyof StorefrontProjectParams],
  ) => void;
}

export default function SignupCustomInput({
  index,
  project,
  changeProject,
  changeProjectParams,
}: SignupCustomInputProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const displayKey = `display_user_custom_${index}` as const;
  const isDisplayed = project.storefront_project_params[displayKey];

  const onChangeTranslations = (
    language: keyof Locales,
    param: `custom_${SignupCustomIndex}`,
    name: keyof SignupCustomField,
    value: string,
  ): void => {
    const stored = project.locales?.[language]?.login?.[param];
    const currentValue: SignupCustomField = stored ? JSON.parse(stored) : {};

    const updatedValue: SignupCustomField = {
      label: name === "label" ? value : currentValue.label,
      place_holder: name === "place_holder" ? value : currentValue.place_holder,
    };

    const locales: Locales = {
      ...project.locales,
      [language]: {
        ...project.locales?.[language],
        login: {
          ...project.locales?.[language]?.login,
          [param]: JSON.stringify(updatedValue),
        },
      },
    };

    changeProject({ ...project, locales });
  };

  return (
    <React.Fragment>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={isDisplayed}
            iconName=""
            name={displayKey}
            size="sm"
            onChange={(value: boolean) =>
              changeProjectParams(displayKey, value)
            }
          />
        </div>
        <div className="editor-form-label">{t("show_custom", { index })}</div>
      </div>
      {isDisplayed ? (
        <div className={`form-user-custom-${index} m-b-30`}>
          <div className="editor-form-signup-custom section-separator">
            <div className="editor-form-section">
              <SignupTranslateFormInput
                field={`custom_${index}`}
                locales={project.locales}
                name="label"
                onChange={onChangeTranslations}
                title={t("label")}
                translations={project.translations}
              />
            </div>
            <div className="editor-form-section">
              <SignupTranslateFormInput
                field={`custom_${index}`}
                locales={project.locales}
                name="place_holder"
                onChange={onChangeTranslations}
                title={t("placeholder")}
                translations={project.translations}
              />
            </div>
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}
